import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of, from } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { Product } from '../interfaces/product.interface';
import { LanguageService } from './language.service';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

export interface LocalizedProduct {
  name: string;
  description: string;
  features: string[];
  benefits: string[];
  ingredients: string[];
  usage: string[];
}

@Injectable({
  providedIn: 'root'
})
export class ProductLocalizationService {
  private productCache = new Map<string, Product>();
  
  constructor(
    private languageService: LanguageService,
    private firestore: Firestore
  ) {}

  /**
   * Get product from database by ID
   */
  private async getProductById(productId: string): Promise<Product | null> {
    // Check cache first
    if (this.productCache.has(productId)) {
      return this.productCache.get(productId)!;
    }

    try {
      const productDoc = await getDoc(doc(this.firestore, 'coreProducts', productId));
      if (productDoc.exists()) {
        const productData = productDoc.data();
        const product: Product = {
          id: productDoc.id,
          ...productData
        } as Product;
        
        // Cache the product
        this.productCache.set(productId, product);
        return product;
      }
    } catch (error) {
      // Error fetching product
    }
    return null;
  }

  /**
   * Enrich product with i18n data from database if missing
   */
  async enrichProductWithI18n(product: any): Promise<Product> {
    if (!product || !product.id) return product;
    
    // If product already has i18n, return as is
    if (product.i18n) return product;
    
    // Try to get from cache or database
    const fullProduct = await this.getProductById(product.id);
    if (fullProduct && fullProduct.i18n) {
      // Return enriched product
      return { ...product, ...fullProduct };
    }
    
    return product;
  }

  /**
   * Get localized product name
   */
  getLocalizedName(product: Product | null): string {
    if (!product) return '';
    const lang = this.languageService.getCurrentLanguage() || 'en';
    return product.i18n?.[lang]?.name || product.name || 'Unknown Product';
  }

  /**
   * Get localized product description
   */
  getLocalizedDescription(product: Product | null): string {
    if (!product) return '';
    const lang = this.languageService.getCurrentLanguage() || 'en';
    return product.i18n?.[lang]?.description || product.description || '';
  }

  /**
   * Get localized product features
   */
  getLocalizedFeatures(product: Product | null): string[] {
    if (!product) return [];
    const lang = this.languageService.getCurrentLanguage() || 'en';
    
    // Get features from localized version first, then fallback to product features
    const localizedFeatures = product.i18n?.[lang]?.features;
    const productFeatures = product.features;
    const features = localizedFeatures || productFeatures;
    
    if (!features) return [];
    
    // Handle both array and object formats
    if (Array.isArray(features)) {
      return features;
    }
    
    // If it's an object with numeric keys, convert to array
    if (typeof features === 'object') {
      return Object.values(features).filter((item): item is string => typeof item === 'string');
    }
    
    return [];
  }

  /**
   * Get localized product benefits
   */
  getLocalizedBenefits(product: Product | null): string[] {
    if (!product) return [];
    const lang = this.languageService.getCurrentLanguage() || 'en';
    
    // Get benefits from localized version first, then fallback to product benefits
    const localizedBenefits = product.i18n?.[lang]?.benefits;
    const productBenefits = product.benefits;
    const benefits = localizedBenefits || productBenefits;
    
    if (!benefits) return [];
    
    // Handle both array and object formats
    if (Array.isArray(benefits)) {
      return benefits;
    }
    
    // If it's an object with numeric keys, convert to array
    if (typeof benefits === 'object') {
      return Object.values(benefits).filter((item): item is string => typeof item === 'string');
    }
    
    return [];
  }

  /**
   * Get localized product ingredients
   */
  getLocalizedIngredients(product: Product | null): string[] {
    if (!product) return [];
    const lang = this.languageService.getCurrentLanguage() || 'en';
    
    // Get ingredients from localized version first, then fallback to product ingredients
    const localizedIngredients = product.i18n?.[lang]?.ingredients;
    const productIngredients = product.ingredients;
    const ingredients = localizedIngredients || productIngredients;
    
    if (!ingredients) return [];
    
    // Handle both array and object formats
    if (Array.isArray(ingredients)) {
      return ingredients;
    }
    
    // If it's an object with numeric keys, convert to array
    if (typeof ingredients === 'object') {
      return Object.values(ingredients).filter((item): item is string => typeof item === 'string');
    }
    
    return [];
  }

  /**
   * Get localized product usage
   */
  getLocalizedUsage(product: Product | null): string[] {
    if (!product) return [];
    const lang = this.languageService.getCurrentLanguage() || 'en';
    
    // Get usage from localized version first, then fallback to product usage
    const localizedUsage = product.i18n?.[lang]?.usage;
    const productUsage = product.usage;
    const usage = localizedUsage || productUsage;
    
    if (!usage) return [];
    
    // Handle both array and object formats
    if (Array.isArray(usage)) {
      return usage;
    }
    
    // If it's an object with numeric keys, convert to array
    if (typeof usage === 'object') {
      return Object.values(usage).filter((item): item is string => typeof item === 'string');
    }
    
    return [];
  }

  /**
   * Get all localized product data as an object
   */
  getLocalizedProduct(product: Product | null): LocalizedProduct {
    return {
      name: this.getLocalizedName(product),
      description: this.getLocalizedDescription(product),
      features: this.getLocalizedFeatures(product),
      benefits: this.getLocalizedBenefits(product),
      ingredients: this.getLocalizedIngredients(product),
      usage: this.getLocalizedUsage(product)
    };
  }

  /**
   * Get reactive localized product data that updates when language changes
   */
  getLocalizedProduct$(product: Product | null): Observable<LocalizedProduct> {
    return this.languageService.currentLanguage$.pipe(
      map(() => this.getLocalizedProduct(product))
    );
  }

  /**
   * Get reactive localized name that updates when language changes
   */
  getLocalizedName$(product: Product | null): Observable<string> {
    return this.languageService.currentLanguage$.pipe(
      map(() => this.getLocalizedName(product))
    );
  }

  /**
   * Get reactive localized description that updates when language changes
   */
  getLocalizedDescription$(product: Product | null): Observable<string> {
    return this.languageService.currentLanguage$.pipe(
      map(() => this.getLocalizedDescription(product))
    );
  }

  /**
   * Get reactive localized benefits that updates when language changes
   */
  getLocalizedBenefits$(product: Product | null): Observable<string[]> {
    return this.languageService.currentLanguage$.pipe(
      map(() => this.getLocalizedBenefits(product))
    );
  }
}
